import {
  DispositionSchema,
  FidelitySchema,
  DocumentKindSchema,
  NpcRelationshipSchema,
  ReliabilitySchema,
  SituationPressureSchema,
  parseProse,
  type Disposition,
  type Fidelity,
  type LearnerViewInPlay,
  type Prose,
  type SessionStateResponse,
} from "@pushback-and-chaos/contracts";

/**
 * DECODE, not derivation.
 *
 * `GET /sessions/:id` returns `SessionStateResponse`, which is NOT
 * `LearnerViewInPlay` — it carries the same information with the closed
 * unions widened to `string` and four fields absent entirely. This module
 * narrows the strings back to their contract enums and fills the four
 * missing fields from the SSE stream where the stream carries them, and
 * with empty otherwise.
 *
 * It computes nothing. It decides nothing. It does not know what a
 * consequence means. Every value it produces is a value the server sent; the
 * only thing added is type information the wire response threw away.
 *
 * The four fields `SessionStateResponse` does not carry, and what happens to
 * each (reported in the GATE writeup, not silently papered over):
 *   - engagement_question — nowhere on the HTTP surface. Left empty; the
 *     in-play UI never renders it, and the recommendation composer shows the
 *     case title instead. This is a real gap.
 *   - current_beat.title  — `currentBeatId` is a raw snake_case identifier
 *     and must never reach the DOM. The `BEAT` envelope's title is threaded
 *     in from the stream: since 2026-10-29 the coded slug line of the new
 *     in-game day, or empty for the endgame and before the first BEAT, and
 *     never an authored beat or event title. Nothing draws it: the story's
 *     headings come from the turns' own chapters (storyteller/chapter-heads.ts).
 *   - open_threads        — arrives as `THREAD_OPENED` envelopes only.
 *   - ruling_ledger       — not exposed by any route. Always empty.
 */

/**
 * Prose is a branded type constructible only through `parseProse`, which
 * rejects the banned mechanical vocabulary. Server narration has already been
 * through `narratorGate`, so it should always pass — and when it does not,
 * that is a server-side Law 2 leak, which is worth making loud rather than
 * crashing the client mid-session. Rejections are recorded here and surfaced
 * in the dev panel; the text is still rendered, because hiding a leak from
 * the person testing for leaks is the worst of the available options.
 */
const leaks: { text: string; terms: readonly string[] }[] = [];

export function recordedProseLeaks(): readonly { text: string; terms: readonly string[] }[] {
  return leaks;
}

export function toProse(raw: string): Prose {
  const parsed = parseProse(raw);
  if (parsed.ok) return parsed.value;
  if (parsed.error.kind === "BANNED_MECHANICAL_VOCABULARY") {
    leaks.push({ text: raw, terms: parsed.error.terms });
  }
  return raw as Prose;
}

/** A widened wire string narrowed back to its enum, or a stated fallback. */
function narrow<T extends string>(
  schema: { safeParse(v: unknown): { success: true; data: T } | { success: false } },
  raw: string | null,
  fallback: T,
): T {
  if (raw === null) return fallback;
  const parsed = schema.safeParse(raw);
  return parsed.success ? parsed.data : fallback;
}

export interface StreamDerived {
  /** From the most recent BEAT envelope: the new day's slug line, or null for the endgame (never an authored title, 2026-10-29). */
  readonly beat: { readonly id: string; readonly title: string | null } | null;
  /** From THREAD_OPENED envelopes, in arrival order. */
  readonly openThreads: readonly string[];
}

/**
 * `hypotheses_on_record` is deliberately left empty rather than populated
 * from `SessionStateResponse.hypotheses`. `ViewHypothesisSchema` pairs the
 * learner's text with a `status: MechanicalNote` — engine-internal
 * justification prose the wire response does not carry — and inventing a
 * status string to satisfy the shape would be exactly the fabrication this
 * client must not do. The learner's own words travel separately, as plain
 * strings, on `InPlayData.hypotheses`: they are the learner's, not the
 * engine's, so they need no Prose branding and carry no mechanical field.
 */
export interface InPlayData {
  /** The session's case (registry slug), so the app shows this case's opening and no other. */
  readonly caseSlug: string | null;
  readonly view: LearnerViewInPlay;
  readonly hypotheses: readonly string[];
  readonly status: string;
  /** The living notes (ADR 0008): established findings and open questions, in the learner's voice. */
  readonly notes: { readonly established: readonly string[]; readonly open: readonly string[] };
  /** The partner's cross-examination, server-derived; null before a recommendation is presented. */
  readonly crossExam: SessionStateResponse["crossExam"];
}

export function toInPlayData(state: SessionStateResponse, stream: StreamDerived): InPlayData {
  return {
    caseSlug: state.caseSlug,
    view: toLearnerViewInPlay(state, stream),
    hypotheses: state.hypotheses.map((h) => h.text),
    status: state.status,
    notes: state.notes,
    crossExam: state.crossExam,
  };
}

export function toLearnerViewInPlay(state: SessionStateResponse, stream: StreamDerived): LearnerViewInPlay {
  return {
    engagement_question: toProse(""),
    narration: state.narration.map((n) => toProse(n.text)),
    documents: state.documentsDelivered.map((d) => ({
      id: d.docId,
      kind: narrow(DocumentKindSchema, d.kind, "MEMO"),
      title: d.title,
      fidelity: narrow(FidelitySchema, d.fidelity, "CLEAN"),
    })),
    facts: state.factsRevealed.map((f) => ({
      id: f.factId,
      text: toProse(f.text),
      reliability: narrow(ReliabilitySchema, f.reliability, "medium"),
    })),
    npcs: state.npcs.map((n) => ({
      id: n.npcName,
      name: n.npcName,
      role: n.role,
      relationship: narrow(NpcRelationshipSchema, n.relationship, "INFORMANT"),
      disposition: narrow(DispositionSchema, n.disposition, "NEUTRAL"),
    })),
    open_threads: stream.openThreads.map(toProse),
    hypotheses_on_record: [],
    ruling_ledger: [],
    situation_pressure: narrow(SituationPressureSchema, state.situationPressure, "CALM"),
    current_beat: {
      id: stream.beat?.id ?? "—",
      title: toProse(stream.beat?.title ?? ""),
    },
  };
}

/**
 * One thread entry as the play surface renders it (fix-phase 3a). Shaped
 * here — NOT in the component — so the surface's props stay free of numeric
 * fields (the rendered-DOM leak test's ForbiddenKeys guard would flag a raw
 * turn number, and rightly so: a visible turn counter is a mechanic).
 */
export interface ThreadEntry {
  readonly key: string;
  /**
   * The in-game day this turn ENDED on, as the server's coded slug line ("Monday, April 6, 8:40 a.m.", never a
   * beat or event title, 2026-10-29); null from a server that does not say. A turn is headed by the day
   * it BEGAN on (chapter-heads.ts), and a new day is where the story turns a page.
   */
  readonly chapter: string | null;
  readonly learnerText: string | null;
  readonly narration: readonly string[];
  readonly documents: readonly { readonly title: string; readonly kind: string }[];
  readonly npcTells: readonly string[];
  /** The opt-in mechanics line for this turn; null unless the server's setting is on. */
  readonly mechanics: string | null;
}

export function toThread(turns: readonly { turn: number; chapter?: string | null | undefined; learnerText: string | null; narration: readonly string[]; documents: readonly { docId: string; kind: string; title: string; fidelity: string }[]; npcTells: readonly string[]; mechanics?: string | null | undefined }[]): readonly ThreadEntry[] {
  return turns.map((t) => ({
    key: `turn-${String(t.turn)}`,
    chapter: t.chapter ?? null,
    learnerText: t.learnerText,
    narration: t.narration.map((n) => String(toProse(n))),
    documents: t.documents.map((d) => ({ title: d.title, kind: d.kind })),
    npcTells: t.npcTells,
    mechanics: t.mechanics ?? null,
  }));
}

export type { Disposition, Fidelity };
